import { engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Symbol } from "../GameComponents/Symbol";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { GameManager } from "../Managers/GameManager";
import { CustomUtils } from "../Utils/CustomUtils";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Paylines } from "../GameComponents/Paylines";
import { Symbols } from "../GameComponents/Symbols";
import { WinAnimatorExtended } from "./WinAnimatorExtended";
import { Communication } from "neo-games";
import { BetManager } from "../Managers/BetManager";
import { AppContext } from "../../Common/AppContext";
import { PlayList } from "../Components/Sounds/PlayList";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorFs extends WinAnimatorExtended {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly MultiplierAnimationFinished: string = "eventMultiplierAnimationFinished";

    public static readonly ShowFinalTableFinished: string = "eventShowFinalTableFinished";

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    private static readonly MultiplierAnimationTimer: string = "multiplierAnimationTimer";

    protected static readonly ShowFinalTableDelayTimer: string = "showFinalTableDelayTimer";

    protected static readonly FinalTableAnimationTimer: string = "finalTableAnimationTimer";

    protected static readonly FinalTableMultiplierAnimationTimer: string = "finalTableMultiplierAnimationTimer";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    private static readonly MultiplierAnimationDuration: number = 60; //in frames;

    private static readonly MultiplierNextDelayDuration: number = 60; //in frames;

    protected static readonly FinalTableCountingAnimationDuration: number = 180; //in frames;

    protected static readonly FinalTableMultiplierAnimationDuration: number = 30; //in frames;

    protected static readonly ShowFinalTableDelay: number = 30; //in frames;

    protected static readonly FinalTableAnimationDuration: number = 60; //in frames;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected finalTable: SwitchElement;

    protected score: SwitchElement;

    protected currentMultiplier: number;

    protected finalCount: number;

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            this.finalTable = this.childs["FinalTable"] as SwitchElement;
            this.score = this.childs["Score"] as SwitchElement;
        }
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        this.currentReels = reelsData;

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            this.removeCurrentSymbol(reelIndex, i);
        }

        // for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
        for (let i = reelsData[reelIndex].symbols.length - 1; i >= 0; i--) {
            this.addCurrentSymbol(reelIndex, i);
        }

        for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
            const symbol = this.currentSymbols[reelIndex][i];
            if (symbol !== null && symbol.id === 13) {
                symbol.goToCase("IMPACT");
                PlayList.play("SND_BaseWildHit");
            }
        }

        for (let i = 0; i <= reelIndex; i++) {
            this.reorderSymbols(i);
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;
            const currentWin = this.getNonMultiplierWin();

            for (let i = 0; i < this.multipliers.length; i++) {
                if (currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }

            return this.currentMultiplierIndex >= 3;
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());

            return false;
        }
    }

    protected showCustomWin(): boolean {
        if (this.hasOnlyCustomWin()) {
            this.emit(WinAnimator.FinishWinAnimator);
            return true;
        }

        return false;
    }

    protected hasOnlyCustomWin(): boolean {
        if (this.currentWins != null) {
            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.currentWins[i].trigger != Communication.Game.WinTrigger.Custom) {
                    return false;
                }
            }
        }

        return true;
    }

    private getNonMultiplierWin(): number {
        let result = this.currentWin;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger == Communication.Game.WinTrigger.Custom) {
                result = parseFloat(NumberUtils.subtract(result, this.currentWins[i].amount));
            }
        }

        return result;
    }

    protected finishFirstWins(): void {
        this.emit(WinAnimator.FinishWinAnimator);

        //due to final table (cycle wins override timer status)
        if (!CustomUtils.isLastFreespin()) {
            this.currentCycle = 0;
            this.cycleWins();
        }
    }

    protected startAnimation(): void {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = false;

        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(parseInt(NumberUtils.devide(this.getNonMultiplierWin(), BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(this.getNonMultiplierWin());
        }
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected startTimer(status: string, duration: number): void {
        this.stopTimer();
        super.startTimer(status, duration);
    }

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorFs.FinalTableMultiplierAnimationTimer:
                this.updateFinalValue();
                break;
            case WinAnimatorFs.ShowFinalTableDelayTimer:
                this.showFinalTableAnimation();
                break;
            case WinAnimatorFs.FinalTableAnimationTimer:
                this.showFinalTableFinished();
                break;
            case WinAnimatorFs.MultiplierAnimationTimer:
                this.multiplierAnimationFinished();
                break;
            default:
                super.onTimerComplete();
        }
    }

    //******************************************************************************************************************
    //  MULTIPLIER
    //******************************************************************************************************************

    showMultiplierAnimation(): void {
        this.addSymbolsToNoWinWhenMultiplier();

        let count = 0;
        let multiplier = this.currentMultiplier;
        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                const symbol = this.currentSymbols[i][j];
                if (symbol != null && symbol.id == CustomUtils.SymbolFsWild) {
                    symbol.setDynamicTime(count * WinAnimatorFs.MultiplierNextDelayDuration);
                    symbol.goToCase("MULTIPLIER");

                    multiplier++;
                    symbol.once(
                        SwitchElement.FinishAnimation,
                        this.updateMultiplierScore.bind(this, "FsTableMtp", multiplier, true),
                    );
                    count++;
                }
            }
        }

        this.startTimer(
            WinAnimatorFs.MultiplierAnimationTimer,
            WinAnimatorFs.MultiplierAnimationDuration + WinAnimatorFs.MultiplierNextDelayDuration * count,
        );
    }

    hasNewMultiplier(): boolean {
        return this.getMultiplier() > this.currentMultiplier;
    }

    private multiplierAnimationFinished(): void {
        if (GameManager.freespinsResponse.currentWin === 0) {
            this.addBackSymbolsFromNoWinWhenMultiplier();
        }
        this.emit(WinAnimatorFs.MultiplierAnimationFinished);
    }

    private getMultiplier(): number {
        if (
            GameManager.freespinsResponse &&
            GameManager.freespinsResponse.freeSpins &&
            GameManager.freespinsResponse.freeSpins["customData"]
        ) {
            return GameManager.freespinsResponse.freeSpins["customData"].totalWinMultiplier;
        }

        return 1;
    }

    private addSymbolsToNoWinWhenMultiplier(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j].id !== 13) {
                    this.noWinSymbolsContainer.addChild(this.currentSymbols[i][j]);
                }
            }
        }
    }

    private addBackSymbolsFromNoWinWhenMultiplier(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j].id !== 13) {
                    this.symbolsContainer.addChild(this.currentSymbols[i][j]);
                }
            }
        }
        for (let i = 0; i < this.currentSymbols.length; i++) {
            this.reorderSymbols(i);
        }
    }

    //******************************************************************************************************************
    //  SCORE
    //******************************************************************************************************************

    public showScore(init: boolean = false): void {
        if (this.score != null) {
            this.initializeScore();

            if (CustomUtils.isLastFreespin()) {
                this.score.goToCase("EMPTY");
            } else {
                this.score.goToCase("SHOW" + (init ? "_INIT" : ""));
            }
        }
    }

    private initializeScore(): void {
        if (this.score != null) {
            this.currentMultiplier = GameManager.freespinsResponse.freeSpins["customData"]
                ? GameManager.freespinsResponse.freeSpins["customData"].totalWinMultiplier
                : 1;
            this.updateScore("FsTableMtp", this.currentMultiplier);
            this.updateScore("FsTableFsCount", GameManager.freespinsResponse.freeSpins.remainingCount);

            const totalWin = GameManager.freespinsResponse.totalWin;
            if (totalWin > 0) {
                this.updateScore("FsTableWin", GameManager.freespinsResponse.totalWin);
            } else {
                const counter = this.getScoreCounter("FsTableWin");
                counter.bitmapText.text = "";
            }
        }
    }

    private updateMultiplierScore(name: string, value: number, popAnim: boolean = false): void {
        this.currentMultiplier = value;
        this.updateScore(name, value, popAnim);
    }

    public updateScore(name: string, value: number, popAnim: boolean = false): void {
        const table = this.score.getChildElementByName(name) as SwitchElement;
        const counter = this.getScoreCounter(name);

        if (counter != null) {
            counter.setBitmapText(value);
        }

        if (table && popAnim) {
            table.goToCase("POP");
        }
    }

    protected getScoreCounter(name: string): ImageTextCounterElement {
        if (this.score != null) {
            const holder = this.score.getChildElementByName(name) as SwitchElement;

            if (holder != null) {
                return holder.getChildElementByName("Counter") as ImageTextCounterElement;
            }
        }

        return null;
    }

    //******************************************************************************************************************
    //  SHOW FINAL TABLE
    //******************************************************************************************************************

    public showFinalTable(init: boolean = false): void {
        if (init) {
            this.showFinalTableAnimation(init);
        } else {
            this.stopTimer();
            this.startTimer(WinAnimatorFs.ShowFinalTableDelayTimer, WinAnimatorFs.ShowFinalTableDelay);
        }
    }

    protected showFinalTableAnimation(init: boolean = false): void {
        if (CustomUtils.isLastFreespin()) {
            if (this.finalTable != null && this.score != null) {
                this.addChild(this.finalTable); //show over animations container
                this.addChild(this.score); //show over animations container

                this.finalTable.goToCase("SHOW_FINAL_TABLE");
                this.score.goToCase("SHOW_FINAL_TABLE");
                PlayList.stop("SND_FSLoop");
                PlayList.play("SND_FSEnd");

                const totalWinValue = GameManager.freespinsResponse.totalWin;
                const multiplierValue = GameManager.freespinsResponse.freeSpins["customData"]
                    ? GameManager.freespinsResponse.freeSpins["customData"].totalWinMultiplier
                    : 1;
                const winValue = parseFloat(NumberUtils.devide(totalWinValue, multiplierValue));

                const scoreValue = this.getScoreCounter("FsTableWin");
                const tableValue = this.finalTable.getChildElementByName("WinCounter") as ImageTextCounterElement;

                if (scoreValue && tableValue) {
                    if (init) {
                        this.score.goToCase("EMPTY");
                        this.finalTable.goToCase("SHOW_FINAL_TABLE_INIT");

                        tableValue.setBitmapText(GameManager.freespinsResponse.totalWin);
                    } else {
                        this.finalCount = 0;
                        PlayList.loop("SND_BaseCreditboxLoop2");
                        scoreValue.start(winValue, 0, WinAnimatorFs.FinalTableCountingAnimationDuration, 0);
                        tableValue.start(0, winValue, WinAnimatorFs.FinalTableCountingAnimationDuration, 0);

                        this.startTimer(
                            WinAnimatorFs.FinalTableMultiplierAnimationTimer,
                            WinAnimatorFs.FinalTableCountingAnimationDuration,
                        );
                    }
                }
            }
        }
    }

    protected updateFinalValue(): void {
        PlayList.stop("SND_BaseCreditboxLoop2");

        const totalWinValue = GameManager.freespinsResponse.totalWin;
        const winValue = parseFloat(NumberUtils.devide(totalWinValue, this.currentMultiplier));

        if (this.finalCount > 0 && this.finalCount < this.currentMultiplier) {
            const tableValue = this.finalTable.getChildElementByName("WinCounter") as ImageTextCounterElement;
            const scoreMultiplier = this.getScoreCounter("FsTableMtp");
            const currentTotalWin = parseFloat(NumberUtils.multiply(winValue, this.finalCount + 1));

            this.score.goToCase("POP");

            scoreMultiplier.setBitmapText(this.currentMultiplier - this.finalCount);
            tableValue.setBitmapText(currentTotalWin);
        }

        this.finalTable.goToCase("POP");

        if (this.finalCount < this.currentMultiplier) {
            PlayList.play("SND_BaseCreditboxEnd");
            this.finalCount++;
            this.startTimer(
                WinAnimatorFs.FinalTableMultiplierAnimationTimer,
                WinAnimatorFs.FinalTableMultiplierAnimationDuration,
            );
        } else {
            PlayList.play("SND_FSTotalWinEnd");
            this.score.goToCase("HIDE");
            this.finalTable.goToCase("FINISH");
            this.startTimer(WinAnimatorFs.FinalTableAnimationTimer, WinAnimatorFs.FinalTableAnimationDuration);
        }
    }

    private showFinalTableFinished(): void {
        this.emit(WinAnimatorFs.ShowFinalTableFinished);
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    resetFinalTable(): void {
        if (this.finalTable != null) {
            this.finalTable.goToCase("EMPTY");
        }

        this.currentMultiplier = 1;
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            const symbol =
                this.currentSymbols[Math.floor(Math.random() * this.reelCount)][
                    Math.floor(Math.random() * this.symbolCount)
                ];

            if (symbol != null && symbol.getCurrentCase() === Symbol.CaseNormal) {
                if (symbol.hasCase(Symbol.CaseIdle)) {
                    symbol.goToCase(Symbol.CaseIdle);
                }
            }
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorFs.ts
 **/